import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../shared/components/Icon';
import { DockMenu } from './DockMenu';
import { DockTabs, useDocumentCommands } from './DockTabs';
import type { DockGroupNode } from './dockModel';
import { describeAddress } from './panelCatalog';
import { useDockElements, useWorkspace, useWorkspaceActions } from './WorkspaceProvider';
import { WorkspaceStartPage } from './WorkspaceStartPage';

/**
 * One pane of the dock: a strip of tabs, the controls that act on the document in front,
 * and the surface the document itself is projected onto.
 *
 * The pane never renders a document. It hands its body element to the registry and the
 * document pool portals the right one in, which is what lets a tab be dragged from one
 * pane to another — or out into a window — without the page inside it reloading.
 */
export function DockPane({ group }: { group: DockGroupNode }) {
  const { t } = useTranslation();
  const state = useWorkspace();
  const { dispatch, travel } = useWorkspaceActions();
  const { registerBody, registerFrame } = useDockElements();
  const commandsFor = useDocumentCommands();
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [documentList, setDocumentList] = useState<{ x: number; y: number } | null>(null);

  const focused = state.activeGroupId === group.id && state.activeWindowId === null;
  const activePanel = group.activePanelId === null ? undefined : state.panels[group.activePanelId];
  const maximized = state.maximizedGroupId === group.id;
  const dimmed = state.maximizedGroupId !== null && !maximized;

  const attachFrame = useCallback((element: HTMLElement | null) => {
    registerFrame(group.id, element);
    return () => registerFrame(group.id, null);
  }, [group.id, registerFrame]);

  const attachBody = useCallback((element: HTMLElement | null) => {
    registerBody(group.id, element);
    return () => registerBody(group.id, null);
  }, [group.id, registerBody]);

  const canGoBack = activePanel !== undefined && activePanel.historyIndex > 0;
  const canGoForward = activePanel !== undefined && activePanel.historyIndex < activePanel.history.length - 1;

  return (
    <section
      className={`dock-pane${focused ? ' dock-pane--focused' : ''}${maximized ? ' dock-pane--maximized' : ''}${dimmed ? ' dock-pane--dimmed' : ''}`}
      ref={attachFrame}
      aria-label={t('workspace.pane')}
      onPointerDownCapture={() => {
        if (!focused) dispatch({ type: 'focusGroup', groupId: group.id });
      }}
    >
      <header className="dock-pane__header">
        <DockTabs hostId={group.id} panelIds={group.panelIds} activePanelId={group.activePanelId} focused={focused} />

        <div className="dock-pane__tools">
          <button
            type="button"
            className="dock-tool"
            disabled={!canGoBack}
            aria-label={t('workspace.back')}
            title={t('workspace.back')}
            onClick={() => activePanel !== undefined && travel(activePanel.id, -1)}
          >
            <Icon name="chevron" className="icon icon--small directional-icon--reverse" />
          </button>
          <button
            type="button"
            className="dock-tool"
            disabled={!canGoForward}
            aria-label={t('workspace.forward')}
            title={t('workspace.forward')}
            onClick={() => activePanel !== undefined && travel(activePanel.id, 1)}
          >
            <Icon name="chevron" className="icon icon--small directional-icon" />
          </button>

          {group.panelIds.length > 1 ? (
            <button
              type="button"
              className="dock-tool"
              aria-label={t('workspace.documentList')}
              title={t('workspace.documentList')}
              onClick={(event) => {
                const rect = event.currentTarget.getBoundingClientRect();
                setDocumentList({ x: rect.left, y: rect.bottom + 4 });
              }}
            >
              <Icon name="chevron" className="icon icon--small directional-icon--down" />
            </button>
          ) : null}

          <span className="dock-pane__divider" aria-hidden="true" />

          <button
            type="button"
            className="dock-tool"
            disabled={activePanel === undefined}
            aria-label={t('workspace.splitBeside')}
            title={t('workspace.splitBeside')}
            onClick={() => {
              if (activePanel === undefined) return;
              if (group.panelIds.length > 1) dispatch({ type: 'movePanel', panelId: activePanel.id, target: { kind: 'group', groupId: group.id, zone: 'end' } });
              else dispatch({ type: 'openAt', address: activePanel.address, target: { kind: 'group', groupId: group.id, zone: 'end' } });
            }}
          >
            <Icon name="splitBeside" className="icon icon--small" />
          </button>
          <button
            type="button"
            className="dock-tool"
            disabled={activePanel === undefined}
            aria-label={t('workspace.splitBelow')}
            title={t('workspace.splitBelow')}
            onClick={() => {
              if (activePanel === undefined) return;
              if (group.panelIds.length > 1) dispatch({ type: 'movePanel', panelId: activePanel.id, target: { kind: 'group', groupId: group.id, zone: 'bottom' } });
              else dispatch({ type: 'openAt', address: activePanel.address, target: { kind: 'group', groupId: group.id, zone: 'bottom' } });
            }}
          >
            <Icon name="splitBelow" className="icon icon--small" />
          </button>
          <button
            type="button"
            className={`dock-tool${maximized ? ' dock-tool--on' : ''}`}
            aria-pressed={maximized}
            aria-label={maximized ? t('workspace.restorePane') : t('workspace.maximizePane')}
            title={maximized ? t('workspace.restorePane') : t('workspace.maximizePane')}
            onClick={() => dispatch({ type: 'toggleMaximize', groupId: group.id })}
          >
            <Icon name={maximized ? 'restore' : 'maximize'} className="icon icon--small" />
          </button>
          <button
            type="button"
            className="dock-tool"
            aria-label={t('workspace.paneMenu')}
            title={t('workspace.paneMenu')}
            onClick={(event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              setMenu({ x: rect.left, y: rect.bottom + 4 });
            }}
          >
            <Icon name="more" className="icon icon--small" />
          </button>
        </div>
      </header>

      <div className="dock-pane__body" ref={attachBody}>
        {group.panelIds.length === 0 ? <WorkspaceStartPage /> : null}
      </div>

      {documentList === null ? null : (
        <DockMenu
          x={documentList.x}
          y={documentList.y}
          label={t('workspace.documentList')}
          items={group.panelIds.flatMap((panelId) => {
            const panel = state.panels[panelId];
            if (panel === undefined) return [];
            const identity = describeAddress(panel.address, t);
            return [{
              id: panelId,
              label: identity.detail === null ? identity.title : `${identity.title} · ${identity.detail}`,
              icon: identity.icon,
              run: () => dispatch({ type: 'activate', panelId }),
            }];
          })}
          onClose={() => setDocumentList(null)}
        />
      )}

      {menu === null ? null : (
        <DockMenu
          x={menu.x}
          y={menu.y}
          label={t('workspace.paneMenu')}
          items={[
            ...(activePanel === undefined ? [] : commandsFor(activePanel.id)),
            {
              id: 'close-pane',
              label: t('workspace.closePane'),
              icon: 'trash',
              danger: true,
              separated: true,
              disabled: group.panelIds.length === 0,
              run: () => dispatch({ type: 'closeGroup', groupId: group.id }),
            },
          ]}
          onClose={() => setMenu(null)}
        />
      )}
    </section>
  );
}
